import * as Sentry from '@sentry/react';
import { selectDeviceInfo } from 'features/application/applicationSlice';
import { openModal } from 'features/modal/modalSlice';
import { localScreenshareToggled, localScreenshareStopped } from 'features/screenshare/actions';
import { selectLocalScreenshareBroadcasting } from 'features/streaming/streamingSlice';
import ToolbarMenuItem from 'features/toolbar/ToolbarMenuItem/ToolbarMenuItem';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { RTCClient } from 'utils/webrtc';
import webrtcAdapter from 'webrtc-adapter';
import { notification } from 'features/notifications/toast/notification';
import { usePermissions } from 'features/permissions/usePermissions';
import { useFeatureFlag } from 'features/feature-set/useFeatureFlag';
import { PermissionTypes } from 'features/permissions/types';
import { selectIsEmbedded, selectMobileScreenshare } from 'features/sdk/sdkStateSlice';
import { sdkMobileScreenshareToggled } from 'features/sdk/actions';
import { isDomException } from 'utils/types';
import { logger } from 'utils/logger';
import { usePiPWindow } from 'components/PiP/PiPProvider';
import { selectAutoPictureInPictureEnabled } from 'features/room/roomSlice';
import { selectIsLockedContentReadOnly } from 'features/layout/features/content/contentSlice';
import { ContentLockedAction } from 'features/layout/features/content/modals/ContentLockedByModeratorModal';
import { openPipDuringScreenPicker } from 'utils/pip/openPipDuringScreenPicker';
import { getAutoPipStrategy } from 'utils/pip/getAutoPipStrategy';

const ScreenshareControl = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const deviceInfo = useAppSelector(selectDeviceInfo);
  const isBroadcasting = useAppSelector(selectLocalScreenshareBroadcasting);
  const mobileScreenshare = useAppSelector(selectMobileScreenshare);
  const autoPipEnabled = useAppSelector(selectAutoPictureInPictureEnabled);
  const isEmbedded = useAppSelector(selectIsEmbedded);

  const { requestPipWindow, closePipWindow, pipWindowSupported, pipWindowOpen } = usePiPWindow();

  const { hasPermissions } = usePermissions();
  const isFeatureEnabled = useFeatureFlag('screenshare');
  const isContentLockedForUser = useAppSelector(selectIsLockedContentReadOnly);

  const permissionsGranted = hasPermissions(PermissionTypes.screenshare);
  const isFeatureAvailable = isFeatureEnabled && permissionsGranted;

  if (!isFeatureAvailable) {
    return null;
  }

  const toggleScreenshare = async () => {
    if (isBroadcasting) {
      if (mobileScreenshare) {
        dispatch(sdkMobileScreenshareToggled());
      } else {
        dispatch(localScreenshareStopped());
      }
      return;
    }

    if (isContentLockedForUser) {
      dispatch(
        openModal('contentLockedByModerator', { contentAction: ContentLockedAction.Screenshare })
      );
      return;
    }

    if (mobileScreenshare) {
      dispatch(sdkMobileScreenshareToggled());
      return;
    }

    const autoPipRequested = pipWindowSupported && autoPipEnabled && !pipWindowOpen;
    const autoPipStrategy = getAutoPipStrategy(isEmbedded);

    let pipRequest: Promise<boolean> | null = null;

    const reportPipFailure = (error: unknown) => {
      logger.remote({ capture: 'pip' }).warn('[ScreenshareControl] PiP request failed', error);

      return false;
    };

    try {
      if (autoPipRequested && autoPipStrategy === 'before') {
        logger
          .remote({ capture: 'pip' })
          .info('[ScreenshareControl] PiP requested before the screen picker');
        pipRequest = requestPipWindow('screenshare').then(() => true, reportPipFailure);
      }

      const savedStream = RTCClient.screensharingFeed.saveStream();

      if (autoPipRequested && autoPipStrategy === 'during') {
        pipRequest = openPipDuringScreenPicker(savedStream, () =>
          requestPipWindow('screenshare')
        ).catch(reportPipFailure);
      }

      await savedStream;
      dispatch(localScreenshareToggled());

      if (autoPipRequested && autoPipStrategy === 'after') {
        logger
          .remote({ capture: 'pip' })
          .info('[ScreenshareControl] PiP requested automatically after the screen picker');
        requestPipWindow('screenshare');
      }
    } catch (e) {
      if (await pipRequest) {
        closePipWindow();
      }

      if (isDomException(e)) {
        if (deviceInfo.os.name === 'Mac OS') {
          if (
            (webrtcAdapter.browserDetails.browser === 'chrome' && e.message.includes('system')) ||
            (webrtcAdapter.browserDetails.browser === 'firefox' && e.name === 'NotFoundError')
          ) {
            dispatch(openModal('screensharePermissionsError'));
          } else {
            notification(t('notifications:screen_permissions_not_granted'));
          }
        } else {
          notification(t('notifications:screen_permissions_not_granted'));
        }
      } else {
        Sentry.captureException(e);
        notification(t('notifications:screen_permissions_not_granted'));
      }
    }
  };

  const tooltip = isBroadcasting
    ? t('room:toolbar.stop_screen_share')
    : t('room:toolbar.screen_share');

  return (
    <ToolbarMenuItem
      id="screenshare"
      overlay={tooltip}
      active={isBroadcasting}
      ariaLabel={tooltip}
      icon="share-screen"
      onClick={toggleScreenshare}
    />
  );
};

export default ScreenshareControl;
